import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Mail, ArrowRight, KeyRound } from 'lucide-react';

const LoginPage = () => {
  const { sendOtp, verifyOtp } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState<string | null>(null);
  const [accountNotFound, setAccountNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAccountNotFound(false);
    setIsLoading(true);

    try {
      await sendOtp(email, false);
      setStep('code');
    } catch (err: any) {
      if (err.message === 'ACCOUNT_NOT_FOUND') {
        setAccountNotFound(true);
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await verifyOtp(email, code);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await sendOtp(email, false);
      setCode('');
    } catch (err: any) {
      if (err.message === 'ACCOUNT_NOT_FOUND') {
        setAccountNotFound(true);
        setStep('email');
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 bg-secondary-100 rounded-full flex items-center justify-center">
              {step === 'email' ? (
                <Mail className="w-7 h-7 text-secondary-600" />
              ) : (
                <KeyRound className="w-7 h-7 text-secondary-600" />
              )}
            </div>
          </div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Connexion à votre compte
          </h2>
          {step === 'email' ? (
            <p className="mt-2 text-center text-sm text-gray-600">
              Ou{' '}
              <Link to="/register" className="font-medium text-secondary-600 hover:text-secondary-500">
                créez un compte gratuitement
              </Link>
            </p>
          ) : (
            <p className="mt-2 text-center text-sm text-gray-600">
              Un code à 6 chiffres a été envoyé à{' '}
              <span className="font-medium text-gray-900">{email}</span>
            </p>
          )}
        </div>

        {accountNotFound && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
            <div className="text-sm text-amber-800">
              Aucun compte n'existe avec cette adresse email.{' '}
              <Link
                to="/register"
                className="font-semibold underline hover:text-amber-900"
              >
                Créez votre compte gratuitement
              </Link>{' '}
              pour accéder à l'application.
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {step === 'email' ? (
          <form className="mt-8 space-y-6" onSubmit={handleSendCode}>
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1">
                Adresse email
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-secondary-500 focus:border-secondary-500 sm:text-sm"
                placeholder="vous@exemple.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`group relative w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-secondary-600 hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500 transition-colors ${
                  isLoading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Envoi en cours...' : (
                  <>
                    Recevoir un code
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleVerifyCode}>
            <div>
              <label htmlFor="otp-code" className="block text-sm font-medium text-gray-700 mb-1">
                Code de vérification
              </label>
              <input
                id="otp-code"
                name="otp-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-md focus:outline-none focus:ring-secondary-500 focus:border-secondary-500 sm:text-sm text-center text-xl tracking-widest font-mono"
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                disabled={isLoading}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || code.length < 6}
                className={`group relative w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-secondary-600 hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500 transition-colors ${
                  isLoading || code.length < 6 ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Vérification...' : 'Se connecter'}
              </button>
            </div>

            <div className="text-center space-y-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={isLoading}
                className="text-sm text-secondary-600 hover:text-secondary-500 disabled:opacity-50"
              >
                Renvoyer un code
              </button>
              <span className="block text-gray-400 text-xs">ou</span>
              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); setError(null); }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Modifier l'adresse email
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
